import type { Identifier } from "ra-core";

import type {
  WhatsAppClockAlertRow,
  WhatsAppConversationFlagsRow,
  WhatsAppInboxMessageRow,
  WhatsAppInboxRow,
  WhatsAppInboxWindowState,
  WhatsAppLeadSignal,
} from "../whatsappTypes";

/**
 * Almikia Epic EW2 (TASK-080): pure inbox and lead-badge rules shared by the Supabase provider
 * (which feeds them rows read through RLS) and the FakeRest demo. The window clocks and costs
 * stay server decisions: here `awaiting_reply` / `unread` mirror the TASK-076 view derivations
 * and the clock alert flags mirror `whatsapp_lead_clock_alerts` (open clock, strictly under the
 * threshold), so the demo and production agree.
 */
const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

/** Unread counts stop being exact past this; the UI shows `9+`. */
export const UNREAD_CAP = 9;

/**
 * A clock alerts while it is OPEN (now strictly before the deadline) and strictly under the
 * threshold: exactly 2 h (or exactly 1 day) left is not yet an alert, 1 ms less is.
 */
export const clockAlertFlags = (
  clocks: { deadline24h: string | null; deadlineCtwa: string | null },
  now: Date,
  thresholds: { hours24Left: number; daysCtwaLeft: number },
): { alert24h: boolean; alertCtwa: boolean } => {
  const left = (deadline: string | null) =>
    deadline == null ? null : new Date(deadline).getTime() - now.getTime();
  const left24 = left(clocks.deadline24h);
  const leftCtwa = left(clocks.deadlineCtwa);
  return {
    alert24h:
      left24 != null && left24 > 0 && left24 < thresholds.hours24Left * HOUR_MS,
    alertCtwa:
      leftCtwa != null &&
      leftCtwa > 0 &&
      leftCtwa < thresholds.daysCtwaLeft * DAY_MS,
  };
};

type ConversationTimes = Pick<
  WhatsAppConversationFlagsRow,
  "last_inbound_at" | "last_outbound_at" | "last_read_at"
>;

const isAfter = (a: string, b: string) =>
  new Date(a).getTime() > new Date(b).getTime();

/** The customer wrote last (after the welcome template or after the last outbound message). */
export const isAwaitingReply = (row: ConversationTimes): boolean =>
  row.last_inbound_at != null &&
  (row.last_outbound_at == null ||
    isAfter(row.last_inbound_at, row.last_outbound_at));

/** An inbound message newer than the last time the conversation was read. */
export const hasUnread = (row: ConversationTimes): boolean =>
  row.last_inbound_at != null &&
  (row.last_read_at == null || isAfter(row.last_inbound_at, row.last_read_at));

/** Inbound messages newer than `lastReadAt`, capped at {@link UNREAD_CAP} + 1. */
export const countUnread = (
  inboundTimes: readonly string[],
  lastReadAt: string | null,
): number =>
  Math.min(
    UNREAD_CAP + 1,
    inboundTimes.filter((at) => lastReadAt == null || isAfter(at, lastReadAt))
      .length,
  );

export const windowStateOf = (state: {
  can_free_text: boolean;
  ctwa_open: boolean;
}): WhatsAppInboxWindowState =>
  state.can_free_text ? "free_text" : state.ctwa_open ? "ctwa" : "closed";

const sameId = (a: Identifier | null | undefined, b: Identifier) =>
  a != null && String(a) === String(b);

/**
 * Builds the inbox rows of one page. `messages` may hold any number of recent messages of the
 * page's conversations; the newest one per conversation is the preview and the inbound ones newer
 * than the last read are the unread count.
 */
export const buildInboxRows = (
  conversations: readonly WhatsAppConversationFlagsRow[],
  windowStates: ReadonlyMap<
    string,
    { can_free_text: boolean; ctwa_open: boolean }
  >,
  messages: readonly WhatsAppInboxMessageRow[],
  leadNames: ReadonlyMap<string, string>,
): WhatsAppInboxRow[] =>
  conversations.map((conversation) => {
    const own = messages
      .filter((message) => sameId(message.conversation_id, conversation.id))
      .sort((a, b) => b.message_at.localeCompare(a.message_at));
    const last = own[0];
    return {
      conversationId: conversation.id,
      dealId: conversation.deal_id,
      leadName:
        conversation.deal_id == null
          ? null
          : (leadNames.get(String(conversation.deal_id)) ?? null),
      unreadCount: hasUnread(conversation)
        ? Math.max(
            1,
            countUnread(
              own
                .filter((message) => message.direction === "in")
                .map((message) => message.message_at),
              conversation.last_read_at,
            ),
          )
        : 0,
      awaitingReply: isAwaitingReply(conversation),
      lastMessage: last
        ? {
            body: last.body,
            type: last.type,
            direction: last.direction,
            at: last.message_at,
          }
        : null,
      lastMessageAt: conversation.last_message_at,
      windowState: windowStateOf(
        windowStates.get(String(conversation.id)) ?? {
          can_free_text: false,
          ctwa_open: false,
        },
      ),
    };
  });

/**
 * The lead-row signals: one entry per lead that has an alert, a customer waiting for an answer
 * or unread messages. Several conversations of one lead merge (any flag wins, counts add up).
 */
export const buildLeadSignals = (
  conversations: readonly WhatsAppConversationFlagsRow[],
  inboundMessages: readonly Pick<
    WhatsAppInboxMessageRow,
    "conversation_id" | "message_at"
  >[],
  alerts: readonly WhatsAppClockAlertRow[],
): WhatsAppLeadSignal[] => {
  const byDeal = new Map<string, WhatsAppLeadSignal>();
  const entry = (dealId: Identifier): WhatsAppLeadSignal => {
    const key = String(dealId);
    const existing = byDeal.get(key);
    if (existing) return existing;
    const created: WhatsAppLeadSignal = {
      dealId,
      alert24h: false,
      alertCtwa: false,
      awaitingReply: false,
      unreadCount: 0,
    };
    byDeal.set(key, created);
    return created;
  };
  for (const conversation of conversations) {
    if (conversation.deal_id == null) continue;
    const awaiting = isAwaitingReply(conversation);
    const unread = hasUnread(conversation)
      ? Math.max(
          1,
          countUnread(
            inboundMessages
              .filter((message) =>
                sameId(message.conversation_id, conversation.id),
              )
              .map((message) => message.message_at),
            conversation.last_read_at,
          ),
        )
      : 0;
    if (!awaiting && unread === 0) continue;
    const signal = entry(conversation.deal_id);
    signal.awaitingReply = signal.awaitingReply || awaiting;
    signal.unreadCount += unread;
  }
  for (const alert of alerts) {
    const signal = entry(alert.deal_id);
    signal.alert24h = signal.alert24h || alert.alert_24h;
    signal.alertCtwa = signal.alertCtwa || alert.alert_ctwa;
  }
  return Array.from(byDeal.values());
};
